import * as React from 'react';

import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import CourseAccomplishmentPageContent from 'bundles/accomplishments/components/course-certificates/CourseAccomplishmentPageContent';
import CourseCertificateDataProvider from 'bundles/accomplishments/components/course-certificates/CourseCertificateDataProvider';
import CertificateImageDataProvider from 'bundles/accomplishments/components/shared/CertificateImageDataProvider';
import { ENABLE_DEV_OWNER_VIEW } from 'bundles/accomplishments/constants';
import { getCertificateSharingLink, getCourseLink } from 'bundles/accomplishments/utils/routingUtils';
import { NotFound } from 'bundles/common/components/withNotFound';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';

import 'css!./__styles__/CourseAccomplishmentPage';

import CourseCertificatePDF from './CourseCertificatePDF';

type Props = {
  code: string;
  certificateImageUrl?: string;
  metaImageUrl?: string;
};

const CourseAccomplishmentPage: React.FC<Props> = ({ code }) => {
  return (
    <CourseCertificateDataProvider certificateCode={code}>
      {({ loading, error, certificateData }) => {
        if (loading) {
          return (
            <div className="rc-CourseAccomplishmentPage" data-testid="verify-records-cert-page">
              <LoadingIcon />
            </div>
          );
        }

        if (error) {
          return <NotFound />;
        }

        /**
         * Membership Error Fallback December 3 2021.
         * there appears to be a data migration problem (suspected but not determined)
         * some learners have lost membership and progress to courses they completed, but they
         * earned certificates. we want them to be able to view / share their certificates in case
         * the progress data is lost somehow.
         * Jira: LP-5885
         * TODO: revisit the UX for this fallback experience
         * */
        if (!certificateData) {
          return <CourseCertificatePDF />;
        }

        const isCertificateOwner = user.get().id === certificateData.userId || ENABLE_DEV_OWNER_VIEW;
        const courseLink = getCourseLink(certificateData.courseSlug);
        const certificateLink = getCertificateSharingLink(false, code);

        return (
          <CertificateImageDataProvider code={code}>
            {({ certificateImageUrl, metaImageUrl }) => (
              <div role="main" className="rc-CourseAccomplishmentPage" data-testid="verify-records-cert-page">
                <CourseAccomplishmentPageContent
                  courseName={certificateData.courseName}
                  userName={certificateData.userName}
                  partnerNames={certificateData.partnerNames}
                  userPhoto={isCertificateOwner && user.get().photo ? user.get().photo : undefined}
                  courseGrade={certificateData.certificateGrade}
                  courseLink={courseLink}
                  includeGrade={!!certificateData.certificateGrade && isCertificateOwner}
                  certificateLink={certificateLink}
                  courseCompletionDate={certificateData.certificateGrantTime}
                  certificateImageUrl={certificateImageUrl}
                  slug={certificateData.courseSlug}
                  isCertificateOwner={isCertificateOwner}
                />
                <CourseraMetatags
                  metaNameAndProperties={{
                    absoluteImageHref: metaImageUrl,
                    description: `This certificate verifies my successful completion of ${certificateData.partnerNames[0]}'s "${certificateData.courseName}" on Coursera`,
                    ogAndTwitterTitle: `Completion Certificate for ${certificateData.courseName}`,
                    disableCourseraDescriptionSuffix: true,
                    disableDescLimit: true,
                  }}
                />
              </div>
            )}
          </CertificateImageDataProvider>
        );
      }}
    </CourseCertificateDataProvider>
  );
};

export default compose<Props, {}>(
  connectToRouter<Props, {}>(({ params: { code } }) => ({
    code,
  }))
)(CourseAccomplishmentPage);
